import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import wordsCounter from "word-counting"
import WebPageImage from "../../images/web-cover.jpg"
import WebLogo from "../../images/logo.png"
const SEO = ({
  title,
  canonical,
  description,
  date,
  dateModified,
  tags,
  image,
  slug,
  categories,
  headline,
  articleBody,
  crumbs,
}) => {
  const { site } = useStaticQuery(
    graphql`
      query SEO {
        site {
          siteMetadata {
            logo
            description
            title
            siteUrl
            author
            locale
            twitterAuthor
            social {
              twitter
            }
          }
        }
      }
    `
  )

  const siteUrl = site.siteMetadata.siteUrl
  const siteNameTitle = site.siteMetadata.title
  const websiteDescription = description || site.siteMetadata.description
  const author = site.siteMetadata.author
  const logo = WebLogo
  const locale = site.siteMetadata.locale
  const twitter = site.siteMetadata.social.twitter
  const twitterAuthor = site.siteMetadata.twitterAuthor
  const Webimage = `https:${image}` || WebPageImage
  const AllTags =
    tags || `Freelancology , blog, News , Shobiz , Sports , Mobile , Packages`

  const schemaWebPage = {
    "@context": "http://schema.org",
    "@id": `${siteUrl}/${slug}`,
    "@type": "WebPage",
    name: siteNameTitle,
    description: websiteDescription,
    image: `${siteUrl}${Webimage}`,
    publisher: {
      type: "blog",
      "@id": siteUrl,
      logo: {
        "@type": "imageObject",
        url: `${siteUrl}${logo}`,
      },
    },
    license: "http://creativecommons.org/licenses/by-nc-sa/3.0/us/deed.en_US",
  }
  // https://jsonld-examples.com/schema.org/code/article-markup.php
  const schemaArticle = {
    "@context": "http://schema.org",
    "@type": "Article",
    author: author,
    datePublished: date,
    datemodified: dateModified,
    mainEntityOfPage: "True",
    headline: headline,
    // https://schema.org/articleSection like a category
    articleSection: `${categories && categories.slice(1, 2)}`,
    image: {
      "@type": "imageObject",
      url: `https:${image}`,
      height: "600",
      width: "800",
    },
    publisher: {
      "@type": "Organization",
      name: siteNameTitle,
      logo: {
        "@type": "imageObject",
        url: `${siteUrl}${logo}`,
      },
    },
    wordCount: `${
      tags && wordsCounter(articleBody, { isHtml: true }).wordsCount
    }`,
    articleBody: articleBody,
  }

  // https://schema.org/BreadcrumbList

  let schemaBreadcrumbs = [{}]

  function createBreadCrumbsSchema(crumbs) {
    let breadCrumbSchema = [{}]

    for (let i = 0; i < crumbs.length; i++) {
      let schema = [
        {
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@id": crumbs[i].pathname,
            name: crumbs[i].crumbLabel,
          },
        },
      ]
      breadCrumbSchema.push(schema)
    }
    breadCrumbSchema.shift(0)
    schemaBreadcrumbs = {
      "@context": "http://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [breadCrumbSchema],
    }
  }

  if (crumbs) {
    createBreadCrumbsSchema(crumbs)
  }

  return (
    <>
      <Helmet>
        {/* https://www.advancedwebranking.com/blog/meta-tags-important-in-seo/   https://metatags.io/*/}
        <html lang={locale} />
        <link rel="canonical" href={`${siteUrl}/${slug}`} />

        <title>
          {title} | {siteNameTitle}
        </title>
        <meta name="description" content={description} />
        <meta property="keywords" content={AllTags} />
        <meta name="image" content={`${siteUrl}${logo}`} />
        <link rel="apple-touch-icon" href="/favicons/apple-touch-icon.png" />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicons/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicons/favicon-16x16.png"
        />
        {/* https://realfavicongenerator.net/ https://brodieclark.com/4-faq-schema-filtering-commandments/ */}
        <link
          rel="mask-icon"
          href="/favicons/safari-pinned-tab.svg"
          color="#F3824A"
        ></link>
        <link
          rel="mask-icon"
          href="/favicons/safari-pinned-tab.svg"
          color="#F3824A"
        ></link>
        <meta name="msapplication-TileColor" content="#F3824A" />
        <meta name="theme-color" content="##F3824A"></meta>
        {/* Open Graph https://ogp.me/ https://developers.facebook.com/docs/sharing/webmasters/ */}
        <meta property="og:locale" content={locale} />
        <meta property="og:site_name" content={siteNameTitle} />

        <meta property="og:url" content={`${siteUrl}/${slug}`} />
        {/* Only "articles" have tags, so check and make an article ... */}
        <meta property="og:type" content={tags ? "article" : "website"} />

        {tags && <meta property="article:published_time" content={date} />}
        {tags && (
          <meta property="article:modified_time" content={dateModified} />
        )}
        {
          tags && (
            // tags.map((tag, i) => (
            <meta property="article:tags" content={tags} key={tags} />
          )
          // ))
        }

        {tags && <meta property="article:author" content={author} />}
        {tags && <meta property="og:image" content={`https:${image}`} />}
        {!tags && (
          <meta property="og:image" content={`${siteUrl}${WebPageImage}`} />
        )}
        {/* Twitter Graph - https://developer.twitter.com/en/docs/tweets/optimize-with-cards/guides/getting-started */}
        <meta name="twitter:card" content="summary"></meta>
        <meta name="robots" content="index, follow"></meta>
        {twitter && <meta name="twitter:site" content={twitter} />}
        {twitterAuthor && (
          <meta name="twitter:creator" content={twitterAuthor} />
        )}
        {!tags && (
          <script type="application/ld+json">
            {JSON.stringify(schemaWebPage)}
          </script>
        )}

        {tags && (
          <script type="application/ld+json">
            {JSON.stringify(schemaArticle, schemaBreadcrumbs)}
          </script>
        )}

        {schemaBreadcrumbs && (
          <script type="application/ld+json">
            {JSON.stringify(schemaBreadcrumbs)}
          </script>
        )}
      </Helmet>
    </>
  )
}

SEO.propTypes = {
  title: PropTypes.string,
  canonical: PropTypes.string,
  description: PropTypes.string,
  date: PropTypes.string,
  dateModified: PropTypes.string,
  tags: PropTypes.array,
  image: PropTypes.string,
  slug: PropTypes.string,
  categories: PropTypes.array,
  headline: PropTypes.string,
  articleBody: PropTypes.string,
  crumbs: PropTypes.array,
}

export default SEO
